import {useState} from 'react';

import {SurgeryConfirmActionEnum} from 'domain/usecases/private/booking-flow/surgery/post-surgery-confirm';
import {logEventBooking} from 'main/analytics/analytics';
import {LogEventBookingEnum} from 'main/analytics/logEventEnum';
import {
	makeDeleteHealthAppointments,
	makePostExamsCancelBooked,
	makePostOdontoCancelBooked,
} from 'main/factories/usecases';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {usePostSurgeryConfirmMutation} from 'presentation/modules/myAppointments/mutations/usePostSurgeryConfirmMutation';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {BookingTypeEnum, EntryPointCodeEnum} from '../../constants';
import {HandleConfirmationParams} from './types';

export function useCancelBooking() {
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {companyPortal} = useAccountDataStore(state => state.accountData);
	const [cancelState, setCancelState] = useState<OperationStateType | null>(null);
	const {mutateAsync: mutateCancelSurgery} = usePostSurgeryConfirmMutation();

	const isError = cancelState === OperationStateTypesEnum.ERROR;
	const isPending = cancelState === OperationStateTypesEnum.PENDING;
	const isDone = cancelState === OperationStateTypesEnum.DONE;
	const isSubmitting = cancelState === OperationStateTypesEnum.SUBMITTING;

	function handleConfirmOperation({bookingType, protocolNumber}: HandleConfirmationParams) {
		setBookingData(prev => ({
			...prev,
			protocolNumber,
			bookingType,
		}));
		setCancelState(OperationStateTypesEnum.DONE);
	}

	async function cancelHealth(bookingNumber: string, isTelehealth = false) {
		try {
			setCancelState(OperationStateTypesEnum.SUBMITTING);

			const {content} = await makeDeleteHealthAppointments().delete({
				number: Number(bookingNumber),
				entryPointCode: EntryPointCodeEnum.V2,
				operatorName: companyPortal?.operatorName,
				operatorEmail: companyPortal?.operatorEmail,
				operatorDocument: companyPortal?.operatorDocument,
			});

			logEventBooking({
				eventName: isTelehealth
					? LogEventBookingEnum.PORTAL_TELE_BOOKING_CANCEL
					: LogEventBookingEnum.PORTAL_HEALTH_BOOKING_CANCEL,
			});

			handleConfirmOperation({
				bookingType: isTelehealth ? BookingTypeEnum.TELEHEALTH : BookingTypeEnum.HEALTH,
				protocolNumber: content.cancelProtocol.toString(),
			});
		} catch (error) {
			setCancelState(OperationStateTypesEnum.ERROR);
		}
	}

	async function cancelOdonto(bookingNumber: string) {
		try {
			setCancelState(OperationStateTypesEnum.SUBMITTING);

			const {protocolo} = await makePostOdontoCancelBooked().post({
				nuConsulta: bookingNumber,
				cdPontoEntrada: EntryPointCodeEnum.V1,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_DENTAL_BOOKING_CANCEL});

			handleConfirmOperation({
				bookingType: BookingTypeEnum.ODONTO,
				protocolNumber: protocolo.toString(),
			});
		} catch (error) {
			setCancelState(OperationStateTypesEnum.ERROR);
		}
	}

	async function cancelExam(bookingNumber: string) {
		try {
			setCancelState(OperationStateTypesEnum.SUBMITTING);

			const {nuProtocolo} = await makePostExamsCancelBooked().post({
				nuExame: bookingNumber,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				nmOperador: companyPortal?.operatorName,
				emailOperador: companyPortal?.operatorEmail,
				documentoOperador: companyPortal?.operatorDocument,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_EXAM_BOOKING_CANCEL});

			handleConfirmOperation({
				bookingType: BookingTypeEnum.EXAM,
				protocolNumber: nuProtocolo.toString(),
			});
		} catch (error) {
			setCancelState(OperationStateTypesEnum.ERROR);
		}
	}

	const cancelSurgery = async (scheduleCode: number) => {
		try {
			setCancelState(OperationStateTypesEnum.SUBMITTING);
			await mutateCancelSurgery({
				scheduleCode,
				option: SurgeryConfirmActionEnum.CANCEL,
			});

			setCancelState(OperationStateTypesEnum.DONE);
		} catch {
			setCancelState(OperationStateTypesEnum.ERROR);
		}
	};

	return {
		cancelHealth,
		cancelExam,
		cancelOdonto,
		cancelState,
		isError,
		isPending,
		isDone,
		isSubmitting,
		setCancelState,
		cancelSurgery,
	};
}
